// ---------------------------------------------------------------
// GlobalSearch.js — Buscador de toda la web (secciones, tareas,
// personas, teléfonos y enlaces de la guía).
//
// RENDIMIENTO: no descarga NADA al arrancar la web. Sólo cuando el
// usuario escribe por primera vez se suscribe a tareas y guía (y a
// usuarios si es admin), y se queda cacheado el resto de la sesión.
// ---------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import { subscribeTasks, repeatLabel } from "../../services/taskService";
import { subscribeGuide, DEFAULT_PHONES } from "../../services/guideService";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { subscribeNotasTurno } from "../../services/shiftNoteService";
import { subscribeAnnouncements } from "../../services/announcementService";
import { subscribeNotes } from "../../services/noteService";
import { db } from "../../firebase";
import { IconSearch } from "./Icons";
import Icon from "../ui/Icon";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", hint: "Resumen del portal" },
  { id: "calendar", label: "Calendario", hint: "Tu calendario personal" },
  { id: "schedule", label: "Horario", hint: "Turnos del mes y del año" },
  { id: "timeclock", label: "Fichaje", hint: "Registro de jornada" },
  { id: "tasks", label: "Tareas", hint: "Tareas de turno" },
  { id: "guide", label: "Guía rápida", hint: "Enlaces y teléfonos" },
  { id: "profile", label: "Mi perfil", hint: "Nivel, logros y resumen anual" },
  { id: "users", label: "Usuarios", hint: "Gestión del equipo", admin: true },
  { id: "import", label: "Importar Excel", hint: "Subir horarios", admin: true },
  { id: "hoursconfig", label: "Config. horas", hint: "Generador de horarios", admin: true },
];

/** Quita acentos y mayúsculas para que "farmacia" encuentre "Farmacía" */
const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export default function GlobalSearch({ isAdmin, uid, onNavigate }) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  // Los datos sólo se piden cuando de verdad se usa el buscador
  const [active, setActive] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [guide, setGuide] = useState(null);
  const [users, setUsers] = useState([]);
  const [notasTurno, setNotasTurno] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [documentos, setDocumentos] = useState([]); // bloc del propio admin
  const [eventos, setEventos] = useState([]);
  const boxRef = useRef(null);

  // Nada de esto se pide hasta que el usuario toca el buscador
  useEffect(() => {
    if (!active) return undefined;
    const un1 = subscribeTasks(setTasks, () => setTasks([]), { uid, isAdmin });
    const un2 = subscribeGuide(setGuide, () => setGuide(null));
    // El directorio, no las fichas: el buscador no necesita correos
    const un3 = onSnapshot(
      collection(db, "directorio"),
      (snap) =>
        setUsers(
          snap.docs
            .map((d) => ({ uid: d.id, ...d.data() }))
            .filter((u) => u.activo !== false)
        ),
      () => setUsers([])
    );
    const un4 = subscribeNotasTurno(setNotasTurno, () => setNotasTurno([]));
    const un5 = subscribeAnnouncements(setAnuncios, () => setAnuncios([]));
    // El calendario es personal: sólo los eventos de quien busca
    const un6 = onSnapshot(
      query(collection(db, "events"), where("userId", "==", uid)),
      (snap) => setEventos(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setEventos([])
    );
    // El bloc de notas sólo lo tienen los administradores, y cada uno
    // el suyo: las reglas ya lo impiden, pero pedirlo sin ser admin
    // sería un error de permisos innecesario en la consola.
    const un7 = isAdmin
      ? subscribeNotes(uid, setDocumentos, () => setDocumentos([]))
      : () => {};
    return () => {
      un1();
      un2();
      un3();
      un4();
      un5();
      un6();
      un7();
    };
  }, [active, uid, isAdmin]);

  // Cerrar al pulsar fuera
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const results = useMemo(() => {
    const q = norm(term).trim();
    if (q.length < 2) return [];
    const out = [];

    // Secciones
    for (const s of SECTIONS) {
      if (s.admin && !isAdmin) continue;
      if (norm(s.label).includes(q) || norm(s.hint).includes(q)) {
        out.push({
          key: `sec-${s.id}`,
          icon: "place",
          title: s.label,
          sub: s.hint,
          go: s.id,
        });
      }
    }

    // Personas
    for (const u of users) {
      const name = u.username || u.displayName || "";
      if (norm(name).includes(q) || norm(u.email).includes(q)) {
        out.push({
          key: `usr-${u.uid}`,
          icon: "person",
          title: name,
          sub: u.role === "admin" ? "Administrador" : "Equipo",
          go: isAdmin ? "users" : "profile",
        });
      }
    }

    // Tareas
    for (const t of tasks) {
      if (norm(t.title).includes(q) || norm(t.desc).includes(q)) {
        out.push({
          key: `task-${t.id}`,
          icon: "clipboard",
          title: t.title,
          sub: repeatLabel(t) || t.date,
          go: "tasks",
        });
      }
    }

    // Teléfonos y enlaces de la guía
    const phones = guide?.phones?.length ? guide.phones : DEFAULT_PHONES;
    for (const p of phones) {
      if (norm(p.name).includes(q) || norm(p.number).includes(q)) {
        out.push({
          key: `tel-${p.name}`,
          icon: "siren",
          title: `${p.name}${p.number ? ` · ${p.number}` : ""}`,
          sub: "Teléfono de la guía",
          go: "guide",
        });
      }
    }
    for (const l of guide?.links || []) {
      if (norm(l.title).includes(q) || norm(l.url).includes(q)) {
        out.push({
          key: `lnk-${l.url}`,
          icon: "link",
          title: l.title,
          sub: "Enlace de la guía",
          go: "guide",
        });
      }
    }

    // Notas del equipo (las vigentes)
    for (const n of notasTurno) {
      if (norm(n.text).includes(q) || norm(n.authorName).includes(q)) {
        out.push({
          key: `nota-${n.id}`,
          icon: "pin",
          title: n.text.length > 60 ? `${n.text.slice(0, 60)}…` : n.text,
          sub: `Nota de ${n.authorName || "el equipo"}`,
          go: "dashboard",
        });
      }
    }

    // Anuncios del tablón
    for (const a of anuncios) {
      if (norm(a.title).includes(q) || norm(a.body).includes(q)) {
        out.push({
          key: `anuncio-${a.id}`,
          icon: "inbox",
          title: a.title,
          sub: `Anuncio de ${a.authorName || "administración"}`,
          go: "dashboard",
        });
      }
    }

    // Bloc de notas: SÓLO del propio admin. Cada uno ve el suyo (las
    // reglas de Firestore lo impiden de todas formas), así que el
    // buscador no puede ser la rendija por la que se cuelen.
    for (const d of documentos) {
      if (norm(d.title).includes(q) || norm(d.body).includes(q)) {
        out.push({
          key: `doc-${d.id}`,
          icon: "edit",
          title: d.title || "Sin título",
          sub: "Bloc de notas",
          go: "notes",
        });
      }
    }

    // Eventos del calendario (los propios)
    for (const ev of eventos) {
      if (norm(ev.title).includes(q) || norm(ev.location).includes(q)) {
        out.push({
          key: `ev-${ev.id}`,
          icon: "clock",
          title: ev.title,
          sub: `${ev.date}${ev.startTime ? ` · ${ev.startTime}` : ""}`,
          go: "calendar",
        });
      }
    }

    return out.slice(0, 12);
  }, [term, tasks, guide, users, isAdmin, notasTurno, anuncios, documentos, eventos]);

  return (
    <div className="topbar-search gsearch" ref={boxRef}>
      <IconSearch />
      <input
        type="text"
        placeholder="Buscar en la web…"
        value={term}
        onFocus={() => {
          setActive(true); // a partir de aquí sí se cargan los datos
          setOpen(true);
        }}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        aria-label="Buscar"
      />
      {term && (
        <button
          className="gsearch-clear"
          onClick={() => setTerm("")}
          aria-label="Limpiar búsqueda"
        >
          <Icon name="close" />
        </button>
      )}

      {open && term.trim().length >= 2 && (
        <div className="gsearch-panel card">
          {results.length === 0 ? (
            <p className="admin-empty">Sin resultados para «{term}».</p>
          ) : (
            results.map((r) => (
              <button
                key={r.key}
                className="gsearch-item"
                onClick={() => {
                  onNavigate?.(r.go);
                  setOpen(false);
                  setTerm("");
                }}
              >
                <span className="gsearch-icon">
                  <Icon name={r.icon} size={16} />
                </span>
                <span className="gsearch-info">
                  <span className="gsearch-title">{r.title}</span>
                  <span className="gsearch-sub">{r.sub}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
